import type { ExerciseTemplate } from '@yuan-math/exercises';
import { fraction, roundTo, toLatex, toText } from '@yuan-math/math';
import { choiceExercise, fractionExercise, numericExercise, options, template, trap } from './authoring.ts';

/**
 * Laws of exponents, and scientific notation built on top of them.
 *
 * Every distractor here is a rule misapplied rather than an arithmetic slip:
 * exponents added where they should be multiplied, bases operated on, a
 * negative exponent read as a negative number. Those are the errors worth
 * naming, because each one is a coherent — and wrong — theory of what a power
 * means.
 */
export const EXPONENT_RULE_TEMPLATES: ExerciseTemplate[] = [
  template({
    id: 'exponent-product-rule',
    conceptId: 'exponent-rules',
    description: 'Multiply powers of the same base.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const base = rng.int(2, difficulty === 2 ? 4 : 6);
      const a = rng.int(2, 4);
      const b = rng.int(2, 4);
      const answer = a + b;

      const { choices, correctId } = options(
        [
          { latex: `${base}^{${answer}}`, correct: true },
          { latex: `${base}^{${a * b}}`, misconceptionId: 'exponents-multiplied-not-added' },
          { latex: `${base * base}^{${answer}}`, misconceptionId: 'exponent-bases-multiplied' },
          { latex: `${base}^{${Math.abs(a - b) === 0 ? a + b + 1 : Math.abs(a - b)}}`, misconceptionId: 'exponents-multiplied-not-added' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Write this as a single power.',
        latex: `${base}^{${a}} \\times ${base}^{${b}}`,
        choices,
        correctId,
        summary: `${a} copies of ${base} and then ${b} more makes ${answer} altogether, so the answer is ${base}^${answer}.`,
        hint: `Write it out: how many ${base}s are being multiplied together in total?`,
        steps: [
          { label: 'Count the factors on each side.', latex: `\\underbrace{${base} \\cdots ${base}}_{${a}} \\times \\underbrace{${base} \\cdots ${base}}_{${b}}` },
          { label: 'Add them.', latex: `${base}^{${a}+${b}} = ${base}^{${answer}}` },
        ],
      });
    },
  }),

  template({
    id: 'exponent-power-rule',
    conceptId: 'exponent-rules',
    description: 'Raise a power to another power.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a = rng.int(2, 5);
      const b = rng.int(2, 5);
      const answer = a * b;

      return numericExercise({
        prompt: 'Simplify to a single power of x. What is the exponent?',
        latex: `\\left(x^{${a}}\\right)^{${b}}`,
        value: answer,
        summary: `${b} copies of x^${a} multiplied together gives ${a} × ${b} = ${answer} copies of x.`,
        hint: `How many x^${a} blocks are there, and how many xs are in each?`,
        steps: [
          { label: 'Write out the outer power.', latex: `x^{${a}} \\times \\cdots \\times x^{${a}} \\quad (${b}\\text{ times})` },
          { label: 'Add the exponents, which is a multiplication.', latex: `x^{${a} \\times ${b}} = x^{${answer}}` },
        ],
        traps: [trap(a + b, 'exponents-added-not-multiplied')],
      });
    },
  }),

  template({
    id: 'exponent-quotient-rule',
    conceptId: 'exponent-rules',
    description: 'Divide powers of the same base.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const a = rng.int(5, difficulty === 4 ? 12 : 8);
      const b = rng.int(2, a - 2);
      const answer = a - b;

      return numericExercise({
        prompt: 'Simplify to a single power of y. What is the exponent?',
        latex: `\\frac{y^{${a}}}{y^{${b}}}`,
        value: answer,
        summary: `${b} of the ${a} factors cancel, leaving ${a} − ${b} = ${answer}.`,
        hint: 'Cancelling a factor from top and bottom removes one from each. How many are left on top?',
        steps: [
          { label: 'Cancel matching factors.', latex: `y^{${a}-${b}}` },
          { label: 'Subtract.', latex: `y^{${answer}}` },
        ],
        traps: [
          trap(a + b, 'exponents-multiplied-not-added'),
          ...(a % b === 0 && a / b !== answer ? [trap(a / b, 'exponents-added-not-multiplied')] : []),
        ],
      });
    },
  }),

  template({
    id: 'exponent-zero-and-negative',
    conceptId: 'exponent-rules',
    description: 'Evaluate a zero or negative exponent.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const base = rng.int(2, difficulty === 4 ? 6 : 4);
      const zero = rng.bool();

      if (zero) {
        return numericExercise({
          prompt: 'Evaluate.',
          latex: `${base}^{0}`,
          value: 1,
          summary: `Every non-zero number to the power zero is 1, because each step down the pattern divides by ${base}.`,
          hint: `${base}³, ${base}², ${base}¹ — each step divides by ${base}. What comes next?`,
          steps: [
            { label: 'Follow the pattern downwards.', latex: `${base}^{2} = ${base ** 2},\\quad ${base}^{1} = ${base}` },
            { label: 'Divide once more.', latex: `${base}^{0} = ${base} \\div ${base} = 1` },
          ],
          traps: [trap(0, 'zero-exponent-is-zero')],
        });
      }

      const power = rng.int(2, 3);
      const value = fraction(1, base ** power);
      return fractionExercise({
        prompt: 'Evaluate, giving your answer as a fraction.',
        latex: `${base}^{-${power}}`,
        value,
        requireSimplified: true,
        summary: `A negative exponent flips the number over: ${base}^-${power} = 1 ÷ ${base}^${power} = ${toText(value)}.`,
        hint: 'A negative exponent does not make the answer negative. What does it do instead?',
        steps: [
          { label: 'A negative exponent means a reciprocal.', latex: `\\frac{1}{${base}^{${power}}}` },
          { label: 'Evaluate the power.', latex: toLatex(value) },
        ],
        traps: [trap(-(base ** power), 'negative-exponent-negative-value')],
      });
    },
  }),

  template({
    id: 'scientific-notation-write',
    conceptId: 'scientific-notation',
    description: 'Write an ordinary number in scientific notation.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const digits = rng.int(11, 99) / 10;
      const large = difficulty === 2 || rng.bool();
      const power = rng.int(2, difficulty === 2 ? 4 : 6);
      const value = large ? digits * 10 ** power : digits / 10 ** power;
      const exponent = large ? power : -power;

      return numericExercise({
        prompt: `Write ${large ? value.toLocaleString('en-GB') : value.toFixed(power + 1)} in scientific notation. What is the power of ten?`,
        value: exponent,
        summary: `The coefficient is ${digits}, and the decimal point moves ${power} places ${large ? 'right' : 'left'} to rebuild the number, so the power is ${exponent}.`,
        hint: 'A number bigger than 10 needs a positive power; a number smaller than 1 needs a negative one.',
        steps: [
          { label: 'Put one non-zero digit before the point.', latex: `${digits} \\times 10^{?}` },
          { label: 'Count the places the point must move back.', latex: `10^{${exponent}}` },
        ],
        traps: [trap(-exponent, 'scientific-notation-direction')],
      });
    },
  }),

  template({
    id: 'scientific-notation-normalise',
    conceptId: 'scientific-notation',
    description: 'Recognise which form is genuinely scientific notation.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const digits = rng.int(11, 99) / 10;
      const power = rng.int(3, 7);

      const { choices, correctId } = options(
        [
          { latex: `${digits} \\times 10^{${power}}`, correct: true },
          { latex: `${roundTo(digits * 10, 1)} \\times 10^{${power - 1}}`, misconceptionId: 'scientific-notation-not-normalised' },
          { latex: `${roundTo(digits / 10, 3)} \\times 10^{${power + 1}}`, misconceptionId: 'scientific-notation-not-normalised' },
          { latex: `${digits} \\times 10^{-${power}}`, misconceptionId: 'scientific-notation-direction' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `Which of these is ${(digits * 10 ** power).toLocaleString('en-GB')} written correctly in scientific notation?`,
        choices,
        correctId,
        summary: `Scientific notation needs exactly one non-zero digit before the decimal point, so the coefficient must be ${digits}.`,
        hint: 'Three of these have the right value. Only one has a coefficient between 1 and 10.',
      });
    },
  }),
];
